import React, { useCallback, useEffect, useState, useRef } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { Box, MenuItem, TextField } from "@mui/material";
import { fetchWorkShift, fetchWorkShiftTypes } from "../../../Apis/Workshift-api";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import { format, parse } from "date-fns";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  WORK_SHIFT_DEFAULT_COLUMNS,
  WORK_SHIFTS_DATAGRID_KEY,
  mapSavedWorkShiftColumns,
  resolveWorkShiftApiField,
} from "./workShiftListConstants";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_work_shift_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({
        field: resolveWorkShiftApiField(field),
        operator,
        value: filterValue,
      });
    }
  });

  return filters;
};

function WorkShiftList() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(WORK_SHIFT_DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [shiftTypeOptions, setShiftTypeOptions] = useState([]);

  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const org = userData?.organization;

  const formatTimeWithAMPM = (timeStr) => {
    try {
      const date = parse(timeStr, "HH:mm:ss", new Date());
      return format(date, "hh:mm a");
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Invalid time:", timeStr);
      return "";
    }
  };

  const convertMinutesToHours = (minutes) => {
    if (!minutes || isNaN(minutes) || minutes < 0) return "0 minutes";

    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    let result = [];

    if (hrs > 0) {
      result.push(`${hrs} ${hrs === 1 ? "h" : "h"}`);
    }

    if (mins > 0) {
      result.push(`${mins} ${mins === 1 ? "min" : "min"}`);
    }

    // If both hrs and mins are 0
    if (result?.length === 0) {
      return "0 minutes";
    }

    return result.join(" ");
  };

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${WORK_SHIFTS_DATAGRID_KEY}_${org.organization_id}`;
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(mapSavedWorkShiftColumns(serverCfg.columns));
          } else {
            setTableConfig(null);
            setConfigColumns(WORK_SHIFT_DEFAULT_COLUMNS);
          }
        } else {
          setTableConfig(null);
          setConfigColumns(WORK_SHIFT_DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(WORK_SHIFT_DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // load workShifts data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: resolveWorkShiftApiField(
              searchParams.get("sortBy") || "organization_work_shift_id"
            ),
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
          organization_work_shift_type_id: searchParams.get(
            "organization_work_shift_type_id"
          ),
          shift_type: searchParams.get("shift_type"),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/workshifts-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchWorkShift(org.organization_id);
          data = oldData?.workshifts?.data || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        let b = data.map((item) => {
          return {
            ...item,
            id: item.organization_work_shift_id,
            work_shift_start_time: formatTimeWithAMPM(
              item.work_shift_start_time
            ),
            work_shift_end_time: formatTimeWithAMPM(item.work_shift_end_time),
            cardname: item.work_shift_name,
            shift_type: item.work_shift_type || item.shift_type?.[0]?.work_shift_type_name,
            location: item.location || item.location_obj?.location_name,
            carddescription: `${formatTimeWithAMPM(item.work_shift_start_time)} — ${formatTimeWithAMPM(item.work_shift_end_time)}`,
            work_duration_minutes: `${convertMinutesToHours(item?.work_duration_minutes)}`,
            is_active: item?.is_active == 1 || item?.is_active === true ? "✔" : "✖",
            break_duration_minutes: `${item?.break_duration_minutes ?? 0} min`,
            work_shift_type: item.work_shift_type || item.shift_type?.work_shift_type_name,
          };
        });
        setShifts(b);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch work shifts:", err);
          toast.error("Failed to fetch work shifts");
          setShifts([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  useEffect(() => {
    if (!org?.organization_id) {
      setShiftTypeOptions([]);
      return undefined;
    }
    let cancelled = false;
    fetchWorkShiftTypes(org.organization_id)
      .then((result) => {
        if (cancelled) return;
        const payload = result?.workshifttype;
        const rows = Array.isArray(payload) ? payload : payload?.data || [];
        setShiftTypeOptions(
          rows
            .map((item) => ({
              id: item?.organization_work_shift_type_id,
              name: String(item?.work_shift_type_name || "").trim(),
            }))
            .filter((item) => item.id != null && item.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => {
        if (!cancelled) setShiftTypeOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  const handleShiftTypeChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("organization_work_shift_type_id", value);
    else next.delete("organization_work_shift_type_id");
    next.delete("shift_type");
    next.set("page", "1");
    setSearchParams(next);
  };

  const selectedShiftType = (() => {
    const raw = String(
      searchParams.get("organization_work_shift_type_id") || ""
    ).trim();
    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
  })();

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", resolveWorkShiftApiField(field));
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );



  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/workshift/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Workshift deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const status = response.status;
          if (status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else if (status === 400 && data.message?.includes("already assigned")) {
            toast.error("This Workshift is already assigned and cannot be deleted.");
          } else if (status === 422) {
            const firstError =
              data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
              "Validation failed.";
            toast.error(firstError);
          } else if (status === 409) {
            toast.error(data.error || "This Workshift is linked to other records.");
          } else {
            toast.error(data.message || data.error || "Failed to delete workshift");
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete workshift"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/work-shift/edit/${item.id || item.organization_work_shift_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/work-shift/view/${item.id || item.organization_work_shift_id}`
      );
    },
    [navigate]
  );



  return (
    <>
      <Layout4
        loading={loading}
        heading={"WorkShifts"}
        btnName={"Add WorkShift"}
        add_action={"SHIFT_ADD"}
        Data={shifts}
        delete_action={"SHIFT_DELETE"}
        tableHeaders={[
          { name: "WorkShift Name", value_key: "work_shift_name" },
          { name: "Shift Start Time", value_key: "work_shift_start_time" },
          { name: "Shift End Time", value_key: "work_shift_end_time" },
          { name: "Work Duration", value_key: "work_duration_minutes" },
          {
            name: "Break Duration (minutes)",
            value_key: "break_duration_minutes",
          },
        ]}
        Icons={[
          <FormatAlignJustifyIcon
            sx={{ fontSize: 60, color: "grey.500", mb: 2 }}
          />,
          <FormatAlignJustifyIcon color="primary" />,
          <NextWeekIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={["WorkShifts", "WorkShifts", "Add WorkShift", "WorkShifts"]}
        Route={"/organization/work-shift"}
        setData={setShifts}
        DeleteFunc={handleDelete}
      />

      <div className="ems-section-divider" />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_work_shifts"
          primaryKey="organization_work_shift_id"
          heading="Workshifts"
          data={shifts}
          sortname="work_shift_name"
          showActions={true}
          Route="/organization/work-shift"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          edit_delete_action={["SHIFT_DELETE", "SHIFT_EDIT"]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_work_shifts"
          loadedBackendConfig={tableConfig}
          loading={loading || loadingConfig}
          toolbarCenterContent={
            <Box sx={{ width: 220, minWidth: 220, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Shift Type"
                value={selectedShiftType}
                onChange={handleShiftTypeChange}
                sx={{
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {shiftTypeOptions.map((type) => (
                  <MenuItem key={String(type.id)} value={String(type.id)}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default WorkShiftList;